import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Clock, Shield, AlertTriangle, CheckCircle, XCircle,
  Activity, Server, RefreshCw,
} from 'lucide-react';
import { api } from '@/lib/api';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';

const SEVERITY_COLORS = {
  BLOCKER: { bg: '#fef2f2', text: '#991b1b', dot: '#dc2626', label: 'BLOCKER' },
  WARNING: { bg: '#fffbeb', text: '#92400e', dot: '#d97706', label: 'WARNING' },
  INFO: { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6', label: 'INFO' },
  high: { bg: '#fef2f2', text: '#991b1b', dot: '#dc2626', label: 'BLOCKER' },
  medium: { bg: '#fffbeb', text: '#92400e', dot: '#d97706', label: 'WARNING' },
  low: { bg: '#f0fdf4', text: '#166534', dot: '#16a34a', label: 'OK' },
};

const TimeIntegrityDashboard = ({ embedded = false }) => {
  const { theme } = useTheme();
  const c = colors[theme];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTimeIntegrity();
      setData(res.data);
    } catch (e) {
      console.error('Error loading time integrity:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className={embedded ? "py-4" : "min-h-screen p-4 md:p-8"} data-testid="time-integrity-loading">
        {!embedded && <h1 className="text-3xl font-bold mb-6" style={{ color: c.textPrimary }}>Time Integrity</h1>}
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: c.accentBlue }} />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { time_integrity_score, mode, mode_label, coverage, contract_compliance, drift_incidents, cutoff_violations, timezone_mismatches, severity_summary = {}, contract_warnings = [] } = data;

  const scoreColor = time_integrity_score >= 80 ? '#16a34a' : time_integrity_score >= 50 ? '#d97706' : '#dc2626';
  const blockers = severity_summary.blockers || 0;
  const warnings = severity_summary.warnings || 0;

  return (
    <div className={embedded ? "py-2" : "min-h-screen p-4 md:p-8"} data-testid="time-integrity-page">
      {/* Header */}
      {!embedded && (
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: c.textPrimary }}>Time Integrity</h1>
          <p className="text-sm mt-1" style={{ color: c.textSecondary }}>{mode_label}</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:opacity-70" style={{ color: c.accentBlue }} data-testid="time-refresh">
          <RefreshCw size={18} />
        </button>
      </motion.div>
      )}
      {embedded && (
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm" style={{ color: c.textSecondary }}>{mode_label}</p>
        <button onClick={load} className="p-1.5 rounded-lg hover:opacity-70" style={{ color: c.accentBlue }} data-testid="time-refresh">
          <RefreshCw size={16} />
        </button>
      </div>
      )}

      {/* Section 1: KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="time-kpis">
        <KpiCard c={c} theme={theme} label="Integrity Score" value={`${time_integrity_score}%`} color={scoreColor} icon={Shield} />
        <KpiCard c={c} theme={theme} label="Blockers" value={blockers} color={blockers > 0 ? '#dc2626' : '#16a34a'} icon={AlertTriangle} />
        <KpiCard c={c} theme={theme} label="Warnings" value={warnings} color={warnings > 0 ? '#d97706' : '#16a34a'} icon={Activity} />
        <KpiCard c={c} theme={theme} label="Engines Compliant" value={`${contract_compliance.compliant}/${contract_compliance.total_engines}`} color={contract_compliance.compliant === contract_compliance.total_engines ? '#16a34a' : '#dc2626'} icon={Server} />
        <KpiCard c={c} theme={theme} label="Cutoff Violations" value={cutoff_violations.length} color={cutoff_violations.length > 0 ? '#d97706' : '#16a34a'} icon={Clock} />
      </div>

      {/* Section: Contract Compliance */}
      <Panel c={c} theme={theme} title="Contract Compliance" icon={Server} testId="contract-compliance">
        <div className="space-y-2">
          {Object.entries(coverage).map(([code, eng]) => (
            <div key={code} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: c.background }} data-testid={`engine-${code}`}>
              <div className="flex items-center gap-3">
                {eng.compliant ? (
                  <CheckCircle size={16} style={{ color: '#16a34a' }} />
                ) : (
                  <XCircle size={16} style={{ color: '#dc2626' }} />
                )}
                <span className="text-sm font-medium" style={{ color: c.textPrimary }}>{eng.label}</span>
              </div>
              <div className="flex items-center gap-3">
                {eng.record_count > 0 && (
                  <span className="text-xs font-mono" style={{ color: c.textSecondary }}>{eng.record_count} records</span>
                )}
                <StatusBadge status={eng.status} compliant={eng.compliant} configured={eng.configured} />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs mt-3" style={{ color: c.textSecondary }}>
          Each engine must expose <code className="px-1 py-0.5 rounded text-[10px]" style={{ background: c.background }}>GET /api/time/records</code> with fields: entity_id, entity_type, event_type, source_id, timestamp_utc, business_date, timezone, cutoff_hour.
        </p>
        {contract_warnings.length > 0 && (
          <div className="mt-3 space-y-1">
            {contract_warnings.map((w, i) => {
              const sev = SEVERITY_COLORS[w.severity] || SEVERITY_COLORS.INFO;
              return (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs" style={{ background: sev.bg, color: sev.text }}>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: sev.text + '15' }}>{w.severity}</span>
                  {w.message}
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* Section: Timezone Consistency */}
      <Panel c={c} theme={theme} title="Timezone Consistency" icon={Clock} testId="timezone-panel">
        {timezone_mismatches.length === 0 ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: '#f0fdf4', color: '#166534' }}>
            <CheckCircle size={14} />
            {contract_compliance.compliant > 0
              ? 'All connected engines report consistent timezone.'
              : 'No engines connected yet — timezone verification pending.'}
          </div>
        ) : (
          <div className="space-y-2">
            {timezone_mismatches.map((m, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: '#fef2f2' }}>
                <span className="text-sm font-medium" style={{ color: '#991b1b' }}>{m.engine}</span>
                <span className="text-xs">
                  Reported: <strong>{m.timezone_reported}</strong> | Expected: <strong>{m.timezone_expected}</strong>
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Section: Cross-Engine Date Consistency (Drift) */}
      <Panel c={c} theme={theme} title="Cross-Engine Date Consistency" icon={Activity} testId="drift-panel">
        {drift_incidents.length === 0 ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: contract_compliance.compliant >= 2 ? '#f0fdf4' : c.background, color: contract_compliance.compliant >= 2 ? '#166534' : c.textSecondary }}>
            {contract_compliance.compliant >= 2 ? (
              <><CheckCircle size={14} /> No date drift detected across connected engines.</>
            ) : (
              <><AlertTriangle size={14} /> Need at least 2 compliant engines to detect cross-engine drift.</>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr style={{ borderBottom: `1px solid ${c.border}` }}>
                  <th className="px-3 py-2 font-semibold" style={{ color: c.textSecondary }}>Timestamp (UTC)</th>
                  {Object.keys(ENGINE_REGISTRY_LABELS).map((e) => (
                    <th key={e} className="px-3 py-2 font-semibold" style={{ color: c.textSecondary }}>{ENGINE_REGISTRY_LABELS[e]}</th>
                  ))}
                  <th className="px-3 py-2 font-semibold" style={{ color: c.textSecondary }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {drift_incidents.map((d, i) => {
                  const sev = SEVERITY_COLORS[d.severity] || SEVERITY_COLORS.medium;
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${c.border}` }} data-testid={`drift-row-${i}`}>
                      <td className="px-3 py-2 font-mono" style={{ color: c.textPrimary }}>{d.timestamp_utc}</td>
                      {Object.keys(ENGINE_REGISTRY_LABELS).map((e) => (
                        <td key={e} className="px-3 py-2 font-mono" style={{ color: d.engines[e] ? c.textPrimary : c.textSecondary }}>
                          {d.engines[e] || '-'}
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: sev.bg, color: sev.text }}>
                          {sev.label || 'DRIFT'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Section: Cutoff Validation */}
      <Panel c={c} theme={theme} title="Cutoff Validation" icon={AlertTriangle} testId="cutoff-panel">
        {cutoff_violations.length === 0 ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: contract_compliance.compliant > 0 ? '#f0fdf4' : c.background, color: contract_compliance.compliant > 0 ? '#166534' : c.textSecondary }}>
            {contract_compliance.compliant > 0 ? (
              <><CheckCircle size={14} /> No cutoff violations detected.</>
            ) : (
              <><Clock size={14} /> Cutoff validation requires compliant engines.</>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr style={{ borderBottom: `1px solid ${c.border}` }}>
                  <th className="px-3 py-2 font-semibold" style={{ color: c.textSecondary }}>Engine</th>
                  <th className="px-3 py-2 font-semibold" style={{ color: c.textSecondary }}>Local Time</th>
                  <th className="px-3 py-2 font-semibold" style={{ color: c.textSecondary }}>Cutoff</th>
                  <th className="px-3 py-2 font-semibold" style={{ color: c.textSecondary }}>Expected</th>
                  <th className="px-3 py-2 font-semibold" style={{ color: c.textSecondary }}>Actual</th>
                  <th className="px-3 py-2 font-semibold" style={{ color: c.textSecondary }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {cutoff_violations.map((v, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${c.border}` }}>
                    <td className="px-3 py-2 font-medium" style={{ color: c.textPrimary }}>{v.engine}</td>
                    <td className="px-3 py-2 font-mono" style={{ color: c.textSecondary }}>{v.local_time}</td>
                    <td className="px-3 py-2 font-mono" style={{ color: c.textSecondary }}>{v.cutoff_hour}:00</td>
                    <td className="px-3 py-2 font-mono" style={{ color: '#16a34a' }}>{v.expected_business_date}</td>
                    <td className="px-3 py-2 font-mono" style={{ color: '#dc2626' }}>{v.actual_business_date}</td>
                    <td className="px-3 py-2"><span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#fef2f2', color: '#991b1b' }}>WRONG</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Footer */}
      <div className="mt-4 text-xs" style={{ color: c.textSecondary }}>
        Checked at {new Date(data.checked_at).toLocaleString()}. Time integrity enforces that all engines agree on business_date, timezone, and cutoff logic.
      </div>
    </div>
  );
};

// ── Sub-components ──

const ENGINE_REGISTRY_LABELS = {
  SALES_CAPTURE: 'Sales',
  ACCOUNTS: 'Accounts',
  INVENTORY: 'Inventory',
  EXPENSE: 'Expense',
};

const KpiCard = ({ c, theme, label, value, color, icon: Icon }) => (
  <motion.div
    initial={{ opacity: 0, y: 5 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-xl p-4"
    style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} style={{ color: c.textSecondary }} />
      <span className="text-xs" style={{ color: c.textSecondary }}>{label}</span>
    </div>
    <span className="text-2xl font-bold" style={{ color }}>{value}</span>
  </motion.div>
);

const Panel = ({ c, theme, title, icon: Icon, children, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 5 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-xl p-5 mb-4"
    style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon size={15} style={{ color: c.accentBlue }} />
      <span className="text-sm font-semibold" style={{ color: c.textPrimary }}>{title}</span>
    </div>
    {children}
  </motion.div>
);

const StatusBadge = ({ status, compliant, configured }) => {
  if (compliant) return <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#f0fdf4', color: '#166534' }}>COMPLIANT</span>;
  if (!configured) return <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#fef2f2', color: '#991b1b' }}>NOT CONFIGURED</span>;
  if (status === 'endpoint_missing') return <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#fef2f2', color: '#991b1b' }}>NOT COMPLIANT</span>;
  if (status === 'unreachable') return <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#fffbeb', color: '#92400e' }}>UNREACHABLE</span>;
  return <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#f1f5f9', color: '#7f8c9a' }}>{status}</span>;
};

export default TimeIntegrityDashboard;
